import {
  Alert,
  Box,
  Button,
  Modal,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { LabelPrinterIds, LotKinds } from '@parallel-fluidics/shared/constants';
import PrinterSelect from 'features/labelPrinters/components/PrinterSelect';
import printLotLabel from 'features/lots/api/printLotLabel';
import renderLotLabel, { buildLotLabelFields } from 'features/lots/utils/renderLotLabel';
import useGetProjectPartByDocId from 'features/pPart/hooks/useGetProjectPartByDocId';
import { useGetProjectByDocId } from 'hooks/use-basicQueries';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

// Assembled lots are labeled in the clean room; everything else (in-house,
// purchased) defaults to the molding-area printer. The user can still pick any
// station in the modal.
const defaultPrinterForLot = (lot) => (
  lot.kind === LotKinds.ASSEMBLED ? LabelPrinterIds.CLEAN_ROOM : LabelPrinterIds.MOLDING
);

/**
 * Print-label modal for the lot detail page. The preview is the exact PNG
 * sent to the printer — same renderer, same pixels (62mm-wide portrait at
 * 300dpi) — so what you see is what prints. Rendering waits for the part
 * (and re-runs once the project name arrives); a failed print keeps the
 * modal open for retry.
 */
export default function PrintLotLabelModal({ lot, onClose }) {
  const { user } = useUserData();
  const [image, setImage] = useState(null);
  const [renderError, setRenderError] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [printer, setPrinter] = useState(() => defaultPrinterForLot(lot));
  const { data: part, isError: partError } = useGetProjectPartByDocId({
    projectDocId: lot.projectDocId,
    pPartDocId: lot.pPartDocId,
  });
  const { data: project } = useGetProjectByDocId(lot.projectDocId);

  useEffect(() => {
    // Wait for the part unless its fetch failed — then render a degraded
    // label (missing part fields show as em dashes) rather than nothing.
    if (!part && !partError) return undefined;
    let cancelled = false;
    renderLotLabel(buildLotLabelFields({ lot, part, projectName: project?.name }))
      .then((rendered) => {
        if (!cancelled) setImage(rendered);
      })
      .catch((error) => {
        if (!cancelled) setRenderError(error.message);
      });
    return () => { cancelled = true; };
  }, [lot, part, partError, project]);

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      await printLotLabel({
        lotId: lot.id,
        image,
        printer,
        user: { uid: user.id, email: user.email },
      });
      toast.success('Label sent to printer');
      onClose();
    } catch (error) {
      // Cap the toast — upstream errors can carry long output (e.g. tracebacks).
      const reason = (error.message || 'unknown error').slice(0, 200);
      toast.warning(`Label print failed: ${reason}`);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`Print label — ${lot.code}`}
      size="large"
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isPrinting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePrint} loading={isPrinting} disabled={!image}>
              Print
            </Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween size="m">
        <PrinterSelect value={printer} onChange={setPrinter} />
        {renderError && <Alert type="error">{`Could not render the label: ${renderError}`}</Alert>}
        {image && (
          <img
            src={`data:image/png;base64,${image}`}
            alt={`Label for ${lot.code}`}
            // Portrait label — cap the height so the preview doesn't dwarf
            // the modal; the aspect ratio stays exact.
            style={{
              display: 'block', margin: '0 auto', maxHeight: '60vh', maxWidth: '100%', border: '1px solid #d5dbdb',
            }}
          />
        )}
        {!image && !renderError && (
          <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
        )}
      </SpaceBetween>
    </Modal>
  );
}

PrintLotLabelModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lot: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
